<template>
  <div class="search-result-item">
    <!-- Added badge -->
    <div
      v-if="added"
      class="flex items-center gap-1 px-3 py-1 bg-green-50 border-b border-green-200 text-green-700 text-sm font-semibold"
    >
      <i class="mdi mdi-check-circle text-base"></i> Added to order
    </div>

    <!-- Product info row -->
    <div
      class="flex items-center gap-3 cursor-pointer p-3 hover:bg-blue-50 rounded-lg transition-colors"
      @click="$emit('select', item)"
    >
      <div class="result-image">
        <img
          v-if="item.image_thumbnail || item.image_url"
          :src="item.image_thumbnail || item.image_url"
          class="w-full h-full object-contain"
        />
        <i v-else class="mdi mdi-image-off text-2xl text-gray-300"></i>
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="text-base md:text-lg font-bold text-gray-900 truncate">
            {{ nkrCode }}
          </span>
          <span
            v-if="item.unificado"
            class="text-sm font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-800 border border-blue-300 shrink-0"
          >
            {{ item.unificado }}
          </span>
        </div>
        <div
          class="text-base md:text-lg text-gray-600 truncate"
          v-if="item._highlightResult && item._highlightResult.title"
          v-html="item._highlightResult.title.value"
        />
        <div v-else class="text-base md:text-lg text-gray-600 truncate">
          {{ item.title }}
        </div>
        <div class="text-xs text-gray-400 mt-0.5">
          <span class="font-semibold text-gray-600">{{ item.brand }}</span>
          <span
            v-if="displayPrice"
            class="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800 border border-green-300"
          >
            {{ displayPrice | currencyFormatted(true) }}
          </span>
        </div>
        <div
          v-if="currentDepartmentStock || otherDepartmentsStores.length > 0"
          class="flex flex-wrap gap-1 mt-1"
        >
          <span
            v-if="currentDepartmentStock"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold border"
            :class="
              currentDepartmentStock.totalStock > 0
                ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                : 'bg-gray-50 text-gray-500 border-gray-300'
            "
            :title="currentDepartmentStock.name"
          >
            <i class="mdi mdi-store mr-0.5"></i>
            {{ currentDepartmentStock.code }}:
            {{ currentDepartmentStock.totalStock || 0 }}
          </span>
          <span
            v-for="entry in otherDepartmentsStores"
            :key="entry.code"
            class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border"
            :class="
              entry.totalStock > 0
                ? 'bg-blue-50 text-blue-700 border-blue-200'
                : 'bg-gray-50 text-gray-400 border-gray-200'
            "
            :title="entry.name"
          >
            {{ entry.code }}: {{ entry.totalStock || 0 }}
          </span>
        </div>
      </div>
      <BaseMiniSpinner v-if="selectLoading" />
      <i v-else class="mdi mdi-chevron-right text-xl text-gray-400"></i>
    </div>

    <!-- Action buttons row -->
    <div class="flex border-t border-gray-100">
      <!-- Quick Add with quantity -->
      <div
        class="flex-1 flex items-stretch border-r border-gray-100"
        @click.stop
      >
        <input
          type="number"
          min="1"
          step="1"
          v-model.number="quantity"
          class="w-14 text-center text-base md:text-lg font-semibold border-r border-gray-100 focus:outline-none focus:bg-green-50"
          @click.stop
        />
        <button
          @click.stop="quickAdd"
          :disabled="loading"
          class="flex-1 text-base md:text-lg font-semibold py-2.5 transition-colors flex items-center justify-center gap-1"
          :class="
            added
              ? 'bg-green-50 text-green-700 hover:bg-green-100'
              : 'text-green-700 hover:bg-green-50'
          "
        >
          <BaseMiniSpinner v-if="loading" />
          <template v-else>
            <i :class="added ? 'mdi mdi-check' : 'mdi mdi-plus'"></i>
            {{ added ? "Added" : "Add" }}
          </template>
        </button>
      </div>

      <!-- View Product Button -->
      <button
        @click.stop="$emit('view', nkrCode)"
        class="flex-1 text-base md:text-lg text-gray-600 hover:text-gray-800 hover:bg-gray-50 py-2.5 transition-colors flex items-center justify-center gap-1"
      >
        <i class="mdi mdi-eye-outline"></i>
        View
      </button>

      <!-- Alternatives Button -->
      <button
        v-if="item.unificado"
        @click.stop="$emit('alternatives', item.unificado)"
        class="flex-1 text-base md:text-lg text-blue-600 hover:text-blue-800 hover:bg-blue-50 py-2.5 border-l border-gray-100 transition-colors flex items-center justify-center gap-1"
      >
        <i class="mdi mdi-magnify"></i>
        Alternatives
      </button>
    </div>
  </div>
</template>

<script>
import { getProducts } from "@/api/store/ProductsService.js";
import { selectBestLine } from "@/utils/product-line.js";

export default {
  name: "SearchResultItem",
  props: {
    item: { type: Object, required: true },
    priceLevel: { type: String, required: true },
    departmentCode: { type: String, required: true },
    transformedPriceLevel: { type: String, required: true },
    selectLoading: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      added: false,
      quantity: 1
    };
  },
  computed: {
    nkrCode() {
      return (this.item.nkrCode && this.item.nkrCode[0]) || this.item.objectID;
    },
    storeEntries() {
      const byStore = this.item.price_and_stock_by_store;
      if (!byStore) return [];
      return Object.entries(byStore).map(([code, entry]) => ({
        ...entry,
        code,
        name: (entry.store && entry.store.nombre) || code
      }));
    },
    currentDepartmentStock() {
      return (
        this.storeEntries.find(entry => entry.code === this.departmentCode) ||
        null
      );
    },
    otherDepartmentsStores() {
      return this.storeEntries.filter(
        entry => entry.code !== this.departmentCode
      );
    },
    displayPrice() {
      if (this.currentDepartmentStock) {
        return this.currentDepartmentStock[this.priceLevel] || null;
      }
      return (
        (this.item.price_and_stock &&
          this.item.price_and_stock[this.priceLevel]) ||
        null
      );
    }
  },
  methods: {
    async quickAdd() {
      if (this.loading) return;
      this.loading = true;
      try {
        const res = await getProducts({
          storeEnabled: "enabled",
          page: 1,
          limitPerPage: "10",
          orderCol: "nkr_code",
          orderDir: "asc",
          search: this.nkrCode,
          requestPrice: true,
          priceLevel: "online",
          departmentCode: this.departmentCode
        });
        const match =
          res.products.find(p => p.nkrCode === this.nkrCode) || res.products[0];
        if (!match) {
          this.$toast.open({
            message: "Product not found in API",
            type: "warning",
            position: "top-right"
          });
          this.loading = false;
          return;
        }
        const emitted = this.buildProduct(match);
        this.loading = false;
        if (emitted) {
          this.added = true;
          this.$emit("quick-add", emitted);
          this.$toast.open({
            message: `${this.nkrCode} added to order`,
            type: "success",
            position: "top-right"
          });
        }
      } catch (e) {
        this.$toast.open({
          message: "Failed to load product details",
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    buildProduct(item) {
      if (item.pack === "KIT") return null;
      const lines = item.pricesAndStock || [];
      const validLines = lines.filter(
        line =>
          line.canSell === true &&
          line.departamentoCodigo === this.departmentCode
      );
      const selectableLines = validLines.filter(
        line => line.warehouse !== "FBA" && line.line !== "OB"
      );
      if (selectableLines.length === 0) {
        this.$toast.open({
          message: `No available stock in your department for ${this.nkrCode}`,
          type: "warning",
          position: "top-right"
        });
        return null;
      }
      const priceKey = this.transformedPriceLevel;
      const best = selectBestLine(selectableLines, priceKey);
      return {
        productId: best.productId,
        nkrCode: item.nkrCode,
        line: best.line,
        description: item.customTitle || item.originalTitle,
        brand: item.brand,
        image: item.image,
        pack: item.pack,
        price: best[priceKey],
        quantity: Math.max(1, parseInt(this.quantity) || 1),
        warehouseId: best.warehouseId,
        warehouseCode: best.warehouse,
        departmentCode: best.departamentoCodigo,
        unificado: item.unificado || null,
        custom: false,
        isTransfer: false,
        pricesAndStock: lines
      };
    }
  }
};
</script>
<style lang="postcss" scoped>
.search-result-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
}

.result-image {
  width: 70px;
  height: 70px;
  flex-shrink: 0;
  background: #f9fafb;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
